import { useMemo, useState } from "react";
import type { CSSProperties, ReactNode } from "react";

import { monthKeyOf, monthLabel, previousMonth } from "../lib/calendarMonth.mjs";
import { useGlobalFilters } from "../lib/globalFilters";
import type { ManifestWidget, RoEfficiencyConfig } from "../lib/manifest";
import {
  daysInMonth,
  divisionDailyGrid,
  driverDailyGrid,
  driverTotals,
  felDriverGrid,
  isWeekend,
  latestDayOnOrBefore,
  lineRows,
  monthStats,
  roDivisionLabel,
  scheduledVsCompleted,
} from "../lib/roEfficiency.mjs";
import type { RoJob, RoRow } from "../lib/roEfficiency.d.mts";
import { useRoRows } from "../lib/roEfficiencyData";
import type { RoSlice } from "../lib/roEfficiencyData";

/**
 * RO Efficiency tab: one panel of the client's Roll Off Efficiency Tracker.
 *
 * The widget arrives resolved (resolve-manifest.mjs applyRoEfficiencyConfig):
 * `widget.ro_efficiency` carries the panel kind, the line, and this
 * customer's own rules. This component reads the page's month and divisions,
 * fetches the service_event slice the panel needs (useRoRows, shared across
 * panels), and draws the tracker's table from the pure arithmetic in
 * roEfficiency.mjs. It never invents a number: a month with no rows says so,
 * a per-day figure with no working-day rule says what it is awaiting, and
 * driver goals say they are awaited.
 */

const LINE_LABEL: Record<string, string> = {
  roll_off: "Roll Off",
  hook_lift: "Hook Lift",
  tractor_trailer: "Tractor Trailer",
  front_load: "Front Load",
};

const WORKING_DAY_GATE = "Awaiting the customer's working-day rule";

const card: CSSProperties = {
  background: "var(--bg-card)",
  border: "1px solid var(--border-subtle)",
  borderRadius: 12,
  padding: 16,
  minWidth: 0,
};
const th: CSSProperties = {
  textAlign: "right",
  padding: "4px 6px",
  fontSize: 11,
  fontWeight: 700,
  color: "var(--text-2)",
  borderBottom: "1px solid var(--border-subtle)",
  whiteSpace: "nowrap",
};
const td: CSSProperties = {
  textAlign: "right",
  padding: "3px 6px",
  fontSize: 12,
  fontVariantNumeric: "tabular-nums",
  whiteSpace: "nowrap",
};
const labelCell: CSSProperties = { ...td, textAlign: "left", fontWeight: 600 };
const weekendBg = "var(--bg-subtle, rgba(0,0,0,0.04))";

function monthBounds(month: string): { from: string; to: string } {
  const days = daysInMonth(month);
  return { from: days[0], to: days[days.length - 1] };
}

function fmtInt(n: number | null | undefined): string {
  return typeof n === "number" ? n.toLocaleString("en-US") : "";
}

function fmtRate(n: number | null | undefined): string {
  return typeof n === "number" && Number.isFinite(n)
    ? n.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 })
    : "";
}

function dayNum(day: string): string {
  return String(Number(day.slice(8, 10)));
}

function Frame(props: {
  widget: ManifestWidget;
  month: string | null;
  children: ReactNode;
  extra?: ReactNode;
}) {
  const { widget, month, children, extra } = props;
  return (
    <section style={card} aria-label={widget.name}>
      <header style={{ display: "flex", gap: 12, alignItems: "baseline", flexWrap: "wrap" }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: "var(--text)" }}>
          {widget.name}
        </h3>
        {month ? (
          <span style={{ fontSize: 12, color: "var(--text-2)" }}>{monthLabel(month)}</span>
        ) : null}
        {extra}
      </header>
      {widget.what_it_shows ? (
        <p style={{ margin: "4px 0 10px", fontSize: 12, color: "var(--text-2)" }}>
          {widget.what_it_shows}
        </p>
      ) : null}
      {children}
      {widget.widget_note ? (
        <p style={{ margin: "10px 0 0", fontSize: 11, color: "var(--text-muted)" }}>
          {widget.widget_note}
        </p>
      ) : null}
    </section>
  );
}

function Note(props: { children: ReactNode }) {
  return (
    <div
      role="status"
      style={{
        padding: "18px 12px",
        fontSize: 13,
        color: "var(--text-2)",
        textAlign: "center",
        border: "1px dashed var(--border-subtle)",
        borderRadius: 10,
      }}
    >
      {props.children}
    </div>
  );
}

function Scroll(props: { children: ReactNode }) {
  return <div style={{ overflowX: "auto", maxWidth: "100%" }}>{props.children}</div>;
}

function DayHeader(props: { days: string[]; first: string; tail?: string[] }) {
  return (
    <thead>
      <tr>
        <th style={{ ...th, textAlign: "left" }}>{props.first}</th>
        {props.days.map((d) => (
          <th key={d} style={{ ...th, background: isWeekend(d) ? weekendBg : undefined }}>
            {dayNum(d)}
          </th>
        ))}
        {(props.tail ?? ["Total"]).map((t) => (
          <th key={t} style={th}>
            {t}
          </th>
        ))}
      </tr>
    </thead>
  );
}

function DayRow(props: {
  label: string;
  days: string[];
  counts: Record<string, number>;
  total: number;
  strong?: boolean;
  onCell?: (day: string) => void;
}) {
  const { label, days, counts, total, strong, onCell } = props;
  const weight = strong ? 800 : undefined;
  return (
    <tr>
      <td style={{ ...labelCell, fontWeight: strong ? 800 : 600 }}>{label}</td>
      {days.map((d) => {
        const v = counts[d];
        const clickable = Boolean(onCell) && typeof v === "number" && v > 0;
        return (
          <td
            key={d}
            style={{
              ...td,
              fontWeight: weight,
              background: isWeekend(d) ? weekendBg : undefined,
              cursor: clickable ? "pointer" : undefined,
              textDecoration: clickable ? "underline dotted" : undefined,
            }}
            onClick={clickable && onCell ? () => onCell(d) : undefined}
          >
            {v ? fmtInt(v) : ""}
          </td>
        );
      })}
      <td style={{ ...td, fontWeight: 800 }}>{fmtInt(total)}</td>
    </tr>
  );
}

function Stat(props: { label: string; value: string; gate?: string | null }) {
  return (
    <div style={{ minWidth: 120 }}>
      <div style={{ fontSize: 11, color: "var(--text-2)", fontWeight: 700 }}>{props.label}</div>
      {props.gate ? (
        <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>{props.gate}</div>
      ) : (
        <div style={{ fontSize: 22, fontWeight: 800, color: "var(--text)" }}>{props.value}</div>
      )}
    </div>
  );
}

function StatsBody(props: {
  rows: RoRow[];
  month: string;
  cfg: RoEfficiencyConfig;
  divisions: string[];
}) {
  const { rows, month, cfg, divisions } = props;
  const s = useMemo(
    () => monthStats(rows, month, cfg.line_config, divisions),
    [rows, month, cfg, divisions],
  );
  const noun = cfg.line === "roll_off" ? "Hauls" : "Pulls";
  const perDayGate = !s.thresholdConfigured
    ? WORKING_DAY_GATE
    : s.workingDays === 0
      ? "No working day this month yet"
      : null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 24 }}>
      <Stat label={`${noun} (no deliveries)`} value={fmtInt(s.hauls)} />
      <Stat label="Deliveries" value={fmtInt(s.deliveries)} />
      <Stat label="Other completed" value={fmtInt(s.other)} />
      <Stat
        label="Working days"
        value={fmtInt(s.workingDays)}
        gate={s.thresholdConfigured ? null : WORKING_DAY_GATE}
      />
      <Stat label={`${noun} per working day`} value={fmtRate(s.haulsPerDay)} gate={perDayGate} />
      <Stat
        label="Deliveries per working day"
        value={fmtRate(s.deliveriesPerDay)}
        gate={perDayGate}
      />
      <Stat label="Average drivers" value={fmtRate(s.avgDrivers)} gate={perDayGate} />
      <Stat label={`${noun} per driver`} value={fmtRate(s.haulsPerDriver)} gate={perDayGate} />
    </div>
  );
}

function JobList(props: { title: string; jobs: RoJob[]; onClose: () => void }) {
  return (
    <div
      style={{
        marginTop: 10,
        border: "1px solid var(--border-subtle)",
        borderRadius: 8,
        padding: 10,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <strong style={{ fontSize: 12 }}>{props.title}</strong>
        <button type="button" onClick={props.onClose} style={{ fontSize: 11 }}>
          Close
        </button>
      </div>
      <table style={{ borderCollapse: "collapse", marginTop: 6 }}>
        <thead>
          <tr>
            <th style={{ ...th, textAlign: "left" }}>Work order</th>
            <th style={{ ...th, textAlign: "left" }}>Route</th>
            <th style={{ ...th, textAlign: "left" }}>Work type</th>
            <th style={{ ...th, textAlign: "left" }}>Counted as</th>
          </tr>
        </thead>
        <tbody>
          {props.jobs.map((j) => (
            <tr key={j.event_id}>
              <td style={{ ...td, textAlign: "left" }}>{j.event_id}</td>
              <td style={{ ...td, textAlign: "left" }}>{j.route}</td>
              <td style={{ ...td, textAlign: "left" }}>{j.work_type}</td>
              <td style={{ ...td, textAlign: "left" }}>{j.category ?? ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p style={{ margin: "6px 0 0", fontSize: 11, color: "var(--text-muted)" }}>
        Site names are awaiting source data: the work order feed we store does not carry the site.
      </p>
    </div>
  );
}

function DriverGridBody(props: {
  rows: RoRow[];
  month: string;
  cfg: RoEfficiencyConfig;
  divisions: string[];
}) {
  const { rows, month, cfg, divisions } = props;
  const [picked, setPicked] = useState<{ label: string; day: string; jobs: RoJob[] } | null>(null);
  const grid = useMemo(
    () => driverDailyGrid(rows, month, cfg.line_config, divisions, cfg.route_rows),
    [rows, month, cfg, divisions],
  );
  return (
    <>
      <Scroll>
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <DayHeader days={grid.days} first="Driver" />
          <tbody>
            {grid.divisionGroups.map((g) => (
              <FragmentRows key={g.division}>
                <tr>
                  <td
                    colSpan={grid.days.length + 2}
                    style={{ ...labelCell, paddingTop: 10, color: "var(--text-2)" }}
                  >
                    {roDivisionLabel(g.division, cfg.division_labels)}
                  </td>
                </tr>
                {g.drivers.map((d) => (
                  <DayRow
                    key={d.label}
                    label={d.label}
                    days={grid.days}
                    counts={d.counts}
                    total={d.total}
                    onCell={(day) => setPicked({ label: d.label, day, jobs: d.jobs[day] ?? [] })}
                  />
                ))}
                <DayRow
                  label={`${roDivisionLabel(g.division, cfg.division_labels)} Total`}
                  days={grid.days}
                  counts={g.totals}
                  total={g.total}
                  strong
                />
              </FragmentRows>
            ))}
            {grid.routes.length > 0 ? (
              <tr>
                <td
                  colSpan={grid.days.length + 2}
                  style={{ ...labelCell, paddingTop: 10, color: "var(--text-2)" }}
                >
                  Route rows
                </td>
              </tr>
            ) : null}
            {grid.routes.map((r) => (
              <DayRow
                key={r.label}
                label={`${r.label} (${r.routes.join(", ")})`}
                days={grid.days}
                counts={r.counts}
                total={r.total}
              />
            ))}
          </tbody>
        </table>
      </Scroll>
      {picked ? (
        <JobList
          title={`${picked.label}, ${picked.day}`}
          jobs={picked.jobs}
          onClose={() => setPicked(null)}
        />
      ) : null}
    </>
  );
}

function FragmentRows(props: { children: ReactNode }) {
  return <>{props.children}</>;
}

function DivisionGridBody(props: {
  rows: RoRow[];
  month: string;
  cfg: RoEfficiencyConfig;
  divisionOrder: string[];
  divisions: string[];
}) {
  const { rows, month, cfg, divisionOrder, divisions } = props;
  const grid = useMemo(
    () => divisionDailyGrid(rows, month, cfg.line_config, divisionOrder, divisions),
    [rows, month, cfg, divisionOrder, divisions],
  );
  return (
    <Scroll>
      <table style={{ borderCollapse: "collapse", width: "100%" }}>
        <DayHeader days={grid.days} first="Division" />
        <tbody>
          {grid.rows.map((r) => (
            <FragmentRows key={r.division}>
              <DayRow
                label={`${roDivisionLabel(r.division, cfg.division_labels)} RO Completed`}
                days={grid.days}
                counts={r.completed}
                total={r.totals.completed}
                strong
              />
              <DayRow
                label={`${roDivisionLabel(r.division, cfg.division_labels)} Hauls`}
                days={grid.days}
                counts={r.hauls}
                total={r.totals.hauls}
              />
              <DayRow
                label={`${roDivisionLabel(r.division, cfg.division_labels)} Deliveries`}
                days={grid.days}
                counts={r.deliveries}
                total={r.totals.deliveries}
              />
            </FragmentRows>
          ))}
          <DayRow
            label="Total Hauls"
            days={grid.days}
            counts={grid.totalHauls}
            total={grid.grand.hauls}
            strong
          />
          <DayRow
            label="Total Deliveries"
            days={grid.days}
            counts={grid.totalDeliveries}
            total={grid.grand.deliveries}
            strong
          />
        </tbody>
      </table>
    </Scroll>
  );
}

function DriverTotalsBody(props: {
  rows: RoRow[];
  month: string;
  cfg: RoEfficiencyConfig;
  divisions: string[];
}) {
  const { rows, month, cfg, divisions } = props;
  const t = useMemo(
    () => driverTotals(rows, month, cfg.line_config, divisions),
    [rows, month, cfg, divisions],
  );
  if (t.byMonth.length === 0)
    return <Note>No completed hauls by a named driver this year yet.</Note>;
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <Scroll>
        <table style={{ borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ ...th, textAlign: "left" }}>Driver</th>
              {t.months.map((m) => (
                <th key={m} style={th}>
                  {monthLabel(m)}
                </th>
              ))}
              <th style={th}>Total</th>
              <th style={th}>Avg per day hauled</th>
            </tr>
          </thead>
          <tbody>
            {t.byMonth.map((d) => (
              <tr key={d.label}>
                <td style={labelCell}>{d.label}</td>
                {t.months.map((m) => (
                  <td key={m} style={td}>
                    {fmtInt(d.hauls[m])}
                  </td>
                ))}
                <td style={{ ...td, fontWeight: 800 }}>{fmtInt(d.total)}</td>
                <td style={td}>{fmtRate(d.avgPerDay)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Scroll>
      <div>
        <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>{monthLabel(month)}</div>
        {t.single.length === 0 ? (
          <Note>No completed roll off jobs by a named driver in {monthLabel(month)}.</Note>
        ) : (
          <Scroll>
            <table style={{ borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ ...th, textAlign: "left" }}>Driver</th>
                  <th style={th}>Hauls</th>
                  <th style={th}>Deliveries</th>
                  <th style={th}>Total jobs</th>
                  <th style={th}>Avg hauls per day hauled</th>
                </tr>
              </thead>
              <tbody>
                {t.single.map((d) => (
                  <tr key={d.label}>
                    <td style={labelCell}>{d.label}</td>
                    <td style={td}>{fmtInt(d.hauls)}</td>
                    <td style={td}>{fmtInt(d.deliveries)}</td>
                    <td style={td}>{fmtInt(d.total)}</td>
                    <td style={td}>{fmtRate(d.avgPerDay)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Scroll>
        )}
      </div>
    </div>
  );
}

function FelBody(props: {
  rows: RoRow[];
  month: string;
  cfg: RoEfficiencyConfig;
  divisions: string[];
}) {
  const { rows, month, cfg, divisions } = props;
  const grid = useMemo(
    () => felDriverGrid(rows, month, cfg.fel, cfg, divisions),
    [rows, month, cfg, divisions],
  );
  if (grid.drivers.length === 0) {
    return <Note>No completed front load deliveries or removals in {monthLabel(month)}.</Note>;
  }
  return (
    <Scroll>
      <table style={{ borderCollapse: "collapse", width: "100%" }}>
        <DayHeader days={grid.days} first="Driver" />
        <tbody>
          {grid.drivers.map((d) => (
            <FragmentRows key={d.label}>
              <DayRow
                label={`${d.label} Deliveries`}
                days={grid.days}
                counts={d.deliveries}
                total={d.totals.deliveries}
              />
              <DayRow
                label={`${d.label} Removals`}
                days={grid.days}
                counts={d.removals}
                total={d.totals.removals}
              />
            </FragmentRows>
          ))}
          <DayRow
            label="Total Deliveries"
            days={grid.days}
            counts={sumByDay(grid.drivers.map((d) => d.deliveries))}
            total={grid.drivers.reduce((a, d) => a + d.totals.deliveries, 0)}
            strong
          />
          <DayRow
            label="Total Removals"
            days={grid.days}
            counts={sumByDay(grid.drivers.map((d) => d.removals))}
            total={grid.drivers.reduce((a, d) => a + d.totals.removals, 0)}
            strong
          />
        </tbody>
      </table>
    </Scroll>
  );
}

function sumByDay(list: Record<string, number>[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const m of list) for (const [d, n] of Object.entries(m)) out[d] = (out[d] ?? 0) + n;
  return out;
}

function ScheduledBody(props: {
  rows: RoRow[];
  month: string;
  cfg: RoEfficiencyConfig;
  divisionOrder: string[];
  divisions: string[];
}) {
  const { rows, month, cfg, divisionOrder, divisions } = props;
  const days = daysInMonth(month);
  const today = new Date();
  const todayKey = `${monthKeyOf(today)}-${String(today.getDate()).padStart(2, "0")}`;
  const fallback = latestDayOnOrBefore(rows, todayKey) ?? days[days.length - 1];
  const [picked, setPicked] = useState<string | null>(null);
  const day = picked && picked.startsWith(month) ? picked : fallback;
  const board = useMemo(
    () => scheduledVsCompleted(rows, day, cfg.line_config, divisionOrder, divisions),
    [rows, day, cfg, divisionOrder, divisions],
  );
  const cols: [string, keyof (typeof board)["total"]][] = [
    ["Scheduled", "scheduled"],
    ["Completed", "completed"],
    ["Drivers", "drivers"],
    ["Hauls scheduled", "haulsScheduled"],
    ["Hauls completed", "haulsCompleted"],
    ["Deliveries scheduled", "deliveriesScheduled"],
    ["Deliveries completed", "deliveriesCompleted"],
  ];
  return (
    <div style={{ display: "grid", gap: 8 }}>
      <label style={{ fontSize: 12, color: "var(--text-2)" }}>
        Date{" "}
        <input
          type="date"
          value={day}
          min={days[0]}
          max={days[days.length - 1]}
          onChange={(e) => setPicked(e.target.value || null)}
        />
      </label>
      {board.rows.length === 0 ? (
        <Note>No roll off jobs on the books for {day}.</Note>
      ) : (
        <Scroll>
          <table style={{ borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...th, textAlign: "left" }}>Division</th>
                {cols.map(([label]) => (
                  <th key={label} style={th}>
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...board.rows, board.total].map((r) => (
                <tr key={r.division}>
                  <td style={{ ...labelCell, fontWeight: r === board.total ? 800 : 600 }}>
                    {roDivisionLabel(r.division, cfg.division_labels)}
                  </td>
                  {cols.map(([label, key]) => (
                    <td
                      key={label}
                      style={{ ...td, fontWeight: r === board.total ? 800 : undefined }}
                    >
                      {fmtInt(r[key] as number)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Scroll>
      )}
    </div>
  );
}

/** The month a panel shows when the page names none: the last complete month. */
export function defaultMonth(today: Date): string {
  return previousMonth(monthKeyOf(today)) ?? monthKeyOf(today);
}

/** The slice of service_event a panel needs, or null when it needs none. */
export function sliceFor(cfg: RoEfficiencyConfig, month: string): RoSlice {
  const { from, to } = monthBounds(month);
  if (cfg.panel === "fel_drivers") {
    return {
      equipmentTypes: ["front_load"],
      from,
      to,
      workTypes: [...cfg.fel.delivery_work_types, ...cfg.fel.removal_work_types],
    };
  }
  if (cfg.panel === "driver_totals") {
    return { equipmentTypes: [cfg.line], from: `${month.slice(0, 4)}-01-01`, to };
  }
  return { equipmentTypes: [cfg.line], from, to };
}

export default function RoEfficiencyPanel(props: {
  widget: ManifestWidget;
  divisionOrder: string[];
}) {
  const { widget, divisionOrder } = props;
  const filters = useGlobalFilters();
  const cfg = widget.ro_efficiency ?? null;
  const blocked = Boolean(widget.status && widget.status !== "ok") || !cfg || !cfg.line_config;
  // No month picked: the last complete month, never the partial current one
  // (a partial month reads as a production drop that is not there).
  const month = filters.month ?? defaultMonth(new Date());
  const slice = blocked || !cfg ? null : sliceFor(cfg, month);
  const { rows, truncated, isLoading, error } = useRoRows(slice);
  const divisions = filters.divisions;
  const scoped = useMemo(
    () => (cfg && cfg.panel !== "fel_drivers" ? lineRows(rows, cfg.line, cfg) : rows),
    [rows, cfg],
  );

  if (blocked || !cfg) {
    return (
      <Frame widget={widget} month={null}>
        <Note>Not available. Awaiting this customer's configuration.</Note>
      </Frame>
    );
  }

  const lineLabel = LINE_LABEL[cfg.line] ?? cfg.line;
  const goalsBadge =
    cfg.panel === "driver_grid" ? (
      <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
        Daily goals: awaiting entry by management
      </span>
    ) : null;

  let body: ReactNode;
  const monthHasRows =
    cfg.panel === "driver_totals"
      ? scoped.some((r) => String(r.event_date ?? "").startsWith(month))
      : scoped.length > 0;
  if (isLoading) {
    body = <Note>Loading {lineLabel} work orders.</Note>;
  } else if (error) {
    body = <Note>Could not read the work orders for this panel. Try reloading the page.</Note>;
  } else if (truncated) {
    body = (
      <Note>
        This month has more work orders than one read returns, so the panel is withheld rather than
        shown short.
      </Note>
    );
  } else if (!monthHasRows && cfg.panel !== "fel_drivers") {
    body = (
      <Note>
        No {lineLabel.toLowerCase()} work orders for {monthLabel(month)} in the data yet.
      </Note>
    );
  } else if (cfg.panel === "stats") {
    body = <StatsBody rows={scoped} month={month} cfg={cfg} divisions={divisions} />;
  } else if (cfg.panel === "driver_grid") {
    body = <DriverGridBody rows={scoped} month={month} cfg={cfg} divisions={divisions} />;
  } else if (cfg.panel === "division_grid") {
    body = (
      <DivisionGridBody
        rows={scoped}
        month={month}
        cfg={cfg}
        divisionOrder={divisionOrder}
        divisions={divisions}
      />
    );
  } else if (cfg.panel === "driver_totals") {
    body = <DriverTotalsBody rows={scoped} month={month} cfg={cfg} divisions={divisions} />;
  } else if (cfg.panel === "fel_drivers") {
    body = <FelBody rows={scoped} month={month} cfg={cfg} divisions={divisions} />;
  } else {
    body = (
      <ScheduledBody
        rows={scoped}
        month={month}
        cfg={cfg}
        divisionOrder={divisionOrder}
        divisions={divisions}
      />
    );
  }

  return (
    <Frame widget={widget} month={month} extra={goalsBadge}>
      {body}
    </Frame>
  );
}
